/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Component, JSXElement, For, Show, createSignal } from 'solid-js';
import DropDownMenu from '../DropDownMenu/DropDownMenu';
import { DropDownOption } from '../DropdownOptions/DropdownOptions';
import { PROJECT_PLACEHOLDER_THUMBNAIL } from '../constants';

export interface CardItem {
  id: string;
  title: string;
  imageUrl?: string;
  isActive?: boolean;
  dropdownOptions?: DropDownOption[];
}

export interface CardsProps {
  /** Array of card items to display */
  cards: CardItem[];
  /** Default image URL when card doesn't have an image */
  defaultImageUrl?: string;
  /** CSS classes for the container */
  className?: string;
  /** Whether cards should be clickable */
  clickable?: boolean;
  /** Column configuration for responsive layout */
  columnClasses?: string;

  // Event callbacks
  onCardClick?(card: CardItem): void;
  onDropdownOptionSelect?(cardId: string, option: string, index: number): void;
}

const Cards: Component<CardsProps> = (props): JSXElement => {
  const [activeDropdownCard, setActiveDropdownCard] = createSignal<
    string | null
  >(null);

  // TODO: Fix type for props.cards to avoid the need for the eslint-disable-next-line
  // Reactive getters for props
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const getCards = () => props.cards || [];
  const getDefaultImageUrl = () =>
    props.defaultImageUrl || PROJECT_PLACEHOLDER_THUMBNAIL;
  const getClassName = () => props.className || '';
  const getClickable = () => props.clickable ?? false;
  const getColumnClasses = () =>
    props.columnClasses || 'col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3';

  const handleCardClick = (card: CardItem) => {
    if (getClickable() && props.onCardClick) {
      props.onCardClick(card);
    }
  };

  const handleDropdownMenuOpen = (cardId: string, isOpen: boolean) => {
    setActiveDropdownCard(isOpen ? cardId : null);
  };

  const handleDropdownOptionSelect = (
    cardId: string,
    selected: string,
    index: number
  ) => {
    setActiveDropdownCard(null);
    if (props.onDropdownOptionSelect) {
      props.onDropdownOptionSelect(cardId, selected, index);
    }
  };

  const renderCard = (card: CardItem) => {
    const mediaObjectClasses = () => {
      const classes = ['media-object'];
      if (card.isActive === true) {
        classes.push('active');
      }
      if (activeDropdownCard() === card.id) {
        classes.push('dropdown-open');
      }
      if (getClickable()) {
        classes.push('clickable');
      }
      return classes.join(' ');
    };

    return (
      <div class={getColumnClasses()}>
        <div
          class={mediaObjectClasses()}
          onClick={() => {
            handleCardClick(card);
          }}
        >
          <div class="thumbnail-wrapper">
            <div class="thumbnail">
              <img
                src={card.imageUrl || getDefaultImageUrl()}
                class="object-fit-cover"
                alt={card.title}
              />
            </div>
          </div>
          <div class="media-object-section">
            <div class="media-object-details">
              <h4 class="text-semibold">{card.title}</h4>
            </div>
            <Show
              when={card.dropdownOptions && card.dropdownOptions.length > 0}
            >
              <div class="media-object-edit">
                <DropDownMenu
                  type="icon"
                  anchorIcon="icon-font tc-icon-more-vert"
                  anchorClassName="default hollow icon-medium icon-square"
                  dropDownOptions={card.dropdownOptions!}
                  optionsType="both"
                  position="right"
                  onMenuOpen={(isOpen) => {
                    handleDropdownMenuOpen(card.id, isOpen);
                  }}
                  onSelectionChanged={(selected, index) => {
                    handleDropdownOptionSelect(card.id, selected, index);
                  }}
                />
              </div>
            </Show>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div class={`tile ${getClassName()}`.trim()}>
      <div class="row">
        <For each={getCards()}>{(card) => renderCard(card)}</For>
      </div>
    </div>
  );
};

export default Cards;
